import React from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Authenticated, NotAuthenticated } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import { Alert, Box, Button, Card, Container, Toolbar } from '@mui/material';

import {
  AdGroupDetailsView,
  AdGroupListView,
  BidModificationHistory,
  CampaignDetailsView,
  CustomerCampaignListView,
  CustomerListView,
} from '~/components/GoogleAds';
import AdCreativeAIAssistantContainer from '~/components/GoogleAds/AdCreativeAIAssistantContainer';
import HotelBidFailureLog from '~/components/GoogleAdsHotelBidding/HotelBidFailureLog';
import HotelBidModificationHistory from '~/components/GoogleAdsHotelBidding/HotelBidModificationHistory';
import BusinessMainPage from '~/components/LeBusinessTraveller/pages/MainPage';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';
import ToursDepartureInsightsPage from '~/components/Tours/Pages/Lists/ToursDepartureInsightsPage';
import CreateOfferDeepLinkForm from '~/components/Users/CreateOfferDeepLinkForm';
import VendorInclusionsPage from '~/components/Vendors/Profile/VendorInclusionsPage';
import RoomTypesGroupsPageContainer from '~/components/Vendors/Properties/RoomTypesGroupsPageContainer';

import CommercialOpsPage from '~/pages/CommercialOps/CommercialOpsPage';
import ShopPage from '~/pages/Shop/Page';
import VoucherDisplayConfigDetailPage from '~/pages/VoucherDisplayConfig/VoucherDisplayConfigDetailPage';
import QuickCreateVoucherPage from '~/pages/Vouchers/QuickCreateVoucherPage';
import VoucherAutoAssignRuleFormPage from '~/pages/Vouchers/VoucherAutoAssignRuleFormPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import { ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_OFFER, ITEM_TYPE_OFFLINE_FLIGHT } from '~/consts/order';
import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import isProd from '~/utils/isProd';

import AccommodationBundleCreatePage from './Accommodation/Pages/Bundles/AccommodationBundleCreatePage';
import AccommodationBundleEditPage from './Accommodation/Pages/Bundles/AccommodationBundleEditPage';
import AccommodationHomePage from './Accommodation/Pages/Home/AccommodationHomePage';
import AccommodationPropertyDetailsPage from './Accommodation/Pages/PropertyDetails/AccommodationPropertyDetailsPage';
import { AiChatDrawer, AiChatProvider } from './AiChat';
import AiSummaryStatsPage from './AiSummaryStats/AiSummaryStatsPage';
import AmenitiesPageContainer from './Amenities/AmenitiesPageContainer';
import AmenityEdit from './Amenities/AmenityEdit';
import AmenityGroupEdit from './Amenities/AmenityGroupEdit';
import AttractionDetailPage from './Attractions/AttractionDetailPage';
import AwsJobsPage from './AwsJobs/Page';
import BedbankPropertiesEditPage from './Bedbank/Properties/Edit/Page';
import BedbankPropertiesPage from './Bedbank/Properties/Home/Page';
import { BookingPage } from './Bookings/BookingPage';
import AppChrome from './Common/AppChrome';
import Footer from './Common/Footer';
import Spotlight from './Common/Spotlight/Spotlight';
import { RouteScanner, SpotlightProvider } from './Common/Spotlight/SpotlightProvider';
import CurationPageContainer from './Content/CurationPageContainer';
import CompaniesListPage from './CorporateEvents/pages/CompaniesListPage';
import CompanyFormPage from './CorporateEvents/pages/CompanyFormPage';
import EventFormPage from './CorporateEvents/pages/EventFormPage';
import EventsListPage from './CorporateEvents/pages/EventsListPage';
import CruiseBundlesCreate from './Cruises/pages/Bundles/BundlesCreate';
import CruiseBundlesEdit from './Cruises/pages/Bundles/BundlesEdit/BundlesEdit';
import CruiseLineEdit from './Cruises/pages/CruiseLines/CruiseLineEdit';
import CruiseLineOrdering from './Cruises/pages/CruiseLines/CruiseLineOrdering/CruiseLineOrdering';
import CruiseDealCrawlerCreate from './Cruises/pages/DealCrawlerPage/DealCrawlerCreate';
import DealCrawlerPageCreationConfirm from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationConfirm';
import DealCrawlerPageCreationSelection from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationSelection';
import {
  DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH,
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
} from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/paths';
import CruiseDealCrawlerView from './Cruises/pages/DealCrawlerPage/DealCrawlerView';
import DealPageCreate from './Cruises/pages/DealsPage/DealPageCreate';
import DealPageEdit from './Cruises/pages/DealsPage/DealPageEdit/DealPageEdit';
import DealPageOrdering from './Cruises/pages/DealsPage/DealPageOrdering';
import DealPageList from './Cruises/pages/DealsPage/components/DealPageList';
import CruisePageImages from './Cruises/pages/Home/CruisePageImages';
import CruisesPage from './Cruises/pages/Home/CruisesPage';
import CruisePageDetails from './Cruises/pages/Home/CruisesPageDetails';
import CruisesBookingLogsPage from './Cruises/pages/Home/components/Bookings/BookingLogsPage';
import CruisesBookingsPage from './Cruises/pages/Home/components/Bookings/BookingsPage';
import CruiseCabinCategoriesDetailsPage from './Cruises/pages/Home/components/CabinCategories/CabinCategoriesDetails';
import CabinCategoriesGroupOverviewPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CabinCategoriesGroupOverview/CabinCategoriesGroupOverviewPage';
import CreateCabinCategoriesGroupPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CreateCabinCategoriesGroup/CreateCabinCategoriesGroup';
import CruiseSailingDetailsPage from './Cruises/pages/Home/components/Sailings/SailingDetailsPage';
import CruiseInclusionsCreate from './Cruises/pages/Inclusions/InclusionsCreate';
import CruiseInclusionsEdit from './Cruises/pages/Inclusions/InclusionsEdit/InclusionsEdit';
import CruisePortsEditPage from './Cruises/pages/Ports/PortsEditPage';
import EditRateCodePage from './Cruises/pages/RateCodeManagement/EditRateCodePage';
import RateCodeManagementPage from './Cruises/pages/RateCodeManagement/RateCodeManagementPage';
import ShipImageEditor from './Cruises/pages/Ships/ShipImageEditor';
import CsDashboardPage from './CsDashboard/CsDashboardPage';
import HeroPlannerScheduleEditorPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerScheduleEditorPage';
import HeroPlannerSchedulesListPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerSchedulesListPage';
import MarketingEmailsPageContainer from './CustomerCommunication/MarketingEmails/MarketingEmailsPageContainer';
import RenderMarketingEmailPage from './CustomerCommunication/MarketingEmails/RenderMarketingEmail';
import EmailPage from './Emails/EmailPage';
import EmailPageContainer from './Emails/EmailPageContainer';
import RenderEmail from './Emails/RenderEmail';
import ExperiencesPage from './Experiences';
import AttractionImageEditor from './Experiences/Attractions/AttractionImageEditor';
import FinancePageContainer from './Finance/FinancePageContainer';
import FinanceOfferDetailContainer from './Finance/OfferDetail/OfferDetail';
import VendorBookingPaymentsContainer from './Finance/OfferEdit/VendorBookingPaymentsContainer';
import VendorOfferPaymentsContainer from './Finance/OfferEdit/VendorOfferPaymentsContainer';
import CreditReservationDetailContainer from './Flights/CreditReservationDetailContainer';
import CreditReservationEditContainer from './Flights/CreditReservationEditContainer';
import CreditReservationListContainer from './Flights/CreditReservationListContainer';
import FlightsDealsEdit from './Flights/FlightDeals/FlightDealsEdit';
import FlightsCampaignsEdit from './Flights/FlightsCampaigns/FlightsCampaignsEdit';
import { FlightsLogsViewer } from './Flights/FlightsLogsViewer/FlightsLogsViewer';
import FlightsPage from './Flights/FlightsPageContainer';
import HealthPage from './Health/HealthPage';
import IndexPage from './IndexPage';
import InvoiceToolsPageContainer from './InvoiceTools/InvoiceToolsPageContainer';
import LereAdminPageContainer from './LERE/LereAdminPageContainer';
import BusinessTabContainer from './LeBusinessTraveller/pages/BusinessDetailsPage/TabContainer';
import LoyaltyPageContainer from './Loyalty/LoyaltyPageContainer';
import BadgesContainer from './Marketing/Badges/BadgesContainer';
import BadgesDetailContainer from './Marketing/Badges/BadgesDetailContainer';
import CompedPackagesDetailContainer from './Marketing/CompedPackages/CompedPackagesDetailContainer';
import MarketingPage from './Marketing/MarketingPage';
import OfferDetailContainer from './Offers/Detail/OfferDetailContainer';
import BadgesEditContainer from './Offers/Edit/BadgesEditContainer';
import BundlePackagesEditContainer from './Offers/Edit/BundlePackagesEditContainer';
import ExperiencesEditContainer from './Offers/Edit/ExperiencesEditContainer';
import ImagesEditContainer from './Offers/Edit/ImagesEditContainer';
import LuxMomentsPage from './Offers/Edit/LuxMomentsPage';
import NewScheduleContainer from './Offers/Edit/NewScheduleContainer';
import OfferEditContainer from './Offers/Edit/OfferEditContainer';
import RoomPolicyEditContainer from './Offers/Edit/RoomPoliciesEditContainer';
import ScheduleEditContainer from './Offers/Edit/ScheduleEditContainer';
import OfferSearchOrderingContainer from './Offers/Edit/SearchOrderingContainer';
import TravellerDetailRequirementsEditPage from './Offers/Edit/TravellerDetailRequirements';
import VideosEditContainer from './Offers/Edit/VideosEditContainer';
import OffersPage from './Offers/Home/OffersPage';
import OpportunityContainer from './Opportunity/OpportunityContainer';
import ExamplePage from './Page/Example/ExamplePage';
import ScheduledPriceSummaryJobDetails from './PriceParrot/ScheduledPriceSummaryJobDetails';
import LoginPage from './Profile/LoginPage';
import ProfilePage from './Profile/ProfilePage';
import OrderEditContainer from './Purchases/Edit/OrderEditContainer';
import PurchasesPageBookingNumberContainer from './Purchases/Home/PurchasesPageBookingNumberContainer';
import PurchasesPageContainer from './Purchases/Home/PurchasesPageContainer';
import OrderAddNotePage from './Purchases/OrderDetail/OrderAddNotePage';
import OrderAtolPageContainer from './Purchases/OrderDetail/OrderAtolPageContainer';
import OrderBookingContainer from './Purchases/OrderDetail/OrderBookingPageContainer';
import OrderGiftEditPage from './Purchases/OrderDetail/OrderGiftEditPage';
import OrderPaymentSurcharge from './Purchases/OrderDetail/OrderPaymentSurcharge';
import SmsVerifyStatsPage from './SmsVerifyStats/SmsVerifyStatsPage';
import UpdateCsAgentsPage from './SmsVerifyStats/UpdateCsAgentsPage';
import SupportAssistantPage from './SupportAssistant/pages/Home/SupportAssistantPage';
import UpdateMonitor from './System/UpdateMonitor';
import TourDealPageCreate from './Tours/Pages/Deals/DealPageCreate';
import TourDealPageEdit from './Tours/Pages/Deals/DealPageEdit';
import TourDealsPage from './Tours/Pages/Deals/TourDealsPage';
import ToursLEListPage from './Tours/Pages/Lists/ToursLEListPage';
import ToursManifestPage from './Tours/Pages/Lists/ToursManifestPage';
import ToursNotificationsPage from './Tours/Pages/Lists/ToursNotificationsPage';
import ToursTTCListPage from './Tours/Pages/Lists/ToursTTCListPage';
import TourDetailsPage from './Tours/Pages/TourDetailsPage';
import CreateTripPage from './TripPlanner/CreateTripPage';
import TripDetailPage from './TripPlanner/TripDetailPage';
import TripPlannerChatPage from './TripPlanner/TripPlannerChatPage';
import TripPlannerClassicPage, { classicTripPlannerTabs } from './TripPlanner/TripPlannerClassicPage';
import TripPlannerPage from './TripPlanner/TripPlannerPage';
import { tripPlannerChatTabs } from './TripPlanner/tripPlannerChatTabs';
import CreditsContainer from './Users/Credits/CreditsContainer';
import FindByPhone from './Users/FindByPhone/FindByPhone';
import UserHistory from './Users/History/UserHistory';
import LoyaltyContainer from './Users/Loyalty/Container';
import AddItemPageContainer from './Users/Orders/AddItem/AddItemPageContainer';
import AtolCertificateDetailPage from './Users/Orders/Atol/AtolCertificateDetailPage';
import AtolCertificateEditPage from './Users/Orders/Atol/AtolCertificateEditPage';
import AtolPage from './Users/Orders/Atol/AtolPage';
import NewOrderPageContainer from './Users/Orders/NewOrder/NewOrderPageContainer';
import UpdateInsuranceContainer from './Users/Orders/UpdateInsurance';
import UserPage from './Users/UserPage';
import UsersPageContainer from './Users/UsersPageContainer';
import { VendorAddonDetails } from './Vendors/Addons/AddonDetails';
import { VendorAddonEdit } from './Vendors/Addons/AddonEdit';
import EditChainSettingsPage from './Vendors/ChainSettings/EditChainSettingsPage';
import ChainDetailPage from './Vendors/Chains/ChainDetailPage';
import ChainsListPage from './Vendors/Chains/ChainsListPage';
import VendorsPageContainer from './Vendors/Home/VendorsPageContainer';
import VendorsPartnerCentralPage from './Vendors/PartnerCentral/VendorsPartnerCentralPage';
import ChainsDetailContainer from './Vendors/Profile/ChainsDetailContainer';
import VendorsDetailContainer from './Vendors/Profile/VendorsDetailContainer';
import CuratedContentEditPage from './Vendors/Properties/CuratedContentEditPage';
import ImagesEditPage from './Vendors/Properties/ImagesEditPage';
import PropertyCommissionableTaxesContentEditPage from './Vendors/Properties/PropertyCommissionableTaxesContentEditPage';
import PropertyEditContainer from './Vendors/Properties/PropertyEditContainer';
import PropertyNewContainer from './Vendors/Properties/PropertyNewContainer';
import PropertyPageContainer from './Vendors/Properties/PropertyPageContainer';
import PropertyReviewsEditContainer from './Vendors/Properties/PropertyReviewsEditContainer';
import PropertyTaxesContentEditPage from './Vendors/Properties/PropertyTaxesContentEditPage';
import PropertyTaxesFeesEditPage from './Vendors/Properties/PropertyTaxesFeesEditPage';
import RoomTypeDatesEditContainer from './Vendors/Properties/RoomTypeDatesEditContainer';
import RoomTypeEditContainer from './Vendors/Properties/RoomTypeEditContainer';
import RoomTypesEditContainer from './Vendors/Properties/RoomTypesEditContainer';
import VideosEditPage from './Vendors/Properties/VideosEditPage';
import RatePlanEditContainer from './Vendors/RatePlans/RatePlanEditContainer';
import RatePlanInclusionsPage from './Vendors/RatePlans/RatePlanInclusionsPage';
import RatePlanNewContainer from './Vendors/RatePlans/RatePlanNewContainer';
import RatePlanPageContainer from './Vendors/RatePlans/RatePlanPageContainer';
import RatePlansGroupEditContainer from './Vendors/RatePlansGroups/RatePlansGroupEditContainer';
import RoomTypesGroupEditContainer from './Vendors/RoomTypesGroups/RoomTypesGroupEditContainer';
import TourEditContainer from './Vendors/Tours/TourEditContainer';
import TourLegsEditContainer from './Vendors/Tours/TourLegsEditContainer';
import TourNewContainer from './Vendors/Tours/TourNewContainer';
import TourOptionDatesEditContainer from './Vendors/Tours/TourOptionDatesEditContainer';
import TourOptionsEditContainer from './Vendors/Tours/TourOptionsEditContainer';
import TourPageContainer from './Vendors/Tours/TourPageContainer';
import TourReviewsEditContainer from './Vendors/Tours/TourReviewsEditContainer';
import MediaPreviewPage from './Videos/MediaPreviewlPage';
import VideoEditPage from './Videos/VideoEditPage';
import {
  AIImageManagerPage,
  AccountDeletionsPage,
  AdminUsersPageContainer,
  AgenciesPage,
  AuditLogSearch,
  BannerUploadPage,
  BriefCasePage,
  BriefCaseTemplatePage,
  BulkCreditAllocationPage,
  BulkVccReplacementPage,
  CSAnalysisPage,
  CreditReversalsPage,
  CustomOffersCreatePage,
  CustomOffersEditPage,
  FindSimilarAccountsPage,
  ImageUploaderUtilityPage,
  LabPage,
  LeDataPage,
  OpenAIPage,
  OrderDetailContainer,
  PackagesEditContainer,
  PermissionsContainer,
  PromoDetailContainerV3,
  ReservationRemediation,
  SalesFxHedging,
  Settings,
  SiteBannerCampaignCreate,
  SiteBannerCampaignEdit,
  SiteBannerCampaignsPage,
  StoplistPage,
  StructuredFinePrintPage,
  SystemPageContainer,
  SystemUserPage,
  TrainingAccountProvisioningPage,
} from './lazyRoutes';

type AppRouteElementProps = {
  routePath: string;
  component?: React.ComponentType<any>;
  render?: (props: any) => React.ReactNode;
};

function AppRouteElement({ routePath, component: Component, render }: AppRouteElementProps) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeProps = {
    match: {
      params,
      path: routePath,
      url: location.pathname,
      isExact: true,
    },
    location,
    history: {
      push: (to, state) => navigate(to, { state }),
      replace: (to, state) => navigate(to, { replace: true, state }),
      go: (delta: number) => navigate(delta),
      goBack: () => navigate(-1),
    },
  };

  if (Component) {
    return <Component {...routeProps} />;
  }

  return <>{render?.(routeProps) ?? null}</>;
}

const TOURS_SUB_NAVIGATIONS: Array<App.NavigationLink> = [
  {
    href: '/tours/lists/le',
    label: 'LE Tours',
  },
  {
    href: '/tours/lists/ttc',
    label: 'TTC Tours',
  },
  {
    href: '/tours/departures/insights',
    label: 'Departures insights',
  },
  {
    href: '/tours/deals',
    label: 'Deals',
  },
  {
    href: '/tours/manifests',
    label: 'Departure Manifests',
  },
  {
    href: '/tours/notifications',
    label: 'DMC Notifications',
  },
];

function isToursNavigationSelected(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function MasterPage() {
  const { tenant } = useCurrentTenant();
  const location = useLocation();
  const isPromoAdmin = usePromoAdminPermission();
  const isProduction = isProd();

  const { pathname } = location;

  const promoAdminOnlyDenied = (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Alert severity="error">
        This page is in testing and only available to users with the {ROLE_PROMO_ADMIN_USER} role. For access, contact
        the growth team.
      </Alert>
    </Container>
  );

  const renderToursContent = (Component: React.ComponentType<any>) => (props: any) => (
    <>
      <Container sx={{ mb: 2 }}>
        <Card variant="outlined">
          <Toolbar>
            {TOURS_SUB_NAVIGATIONS.map((nav) => {
              const isSelected = isToursNavigationSelected(pathname, nav.href);

              return (
                <Button
                  key={nav.href}
                  component={NavLink}
                  to={nav.href}
                  color={isSelected ? 'primary' : 'secondary'}
                  variant={isSelected ? 'contained' : 'text'}
                  size="large"
                  sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                >
                  {nav.label}
                </Button>
              );
            })}
          </Toolbar>
        </Card>
      </Container>

      <Component {...props} />
    </>
  );

  return (
    <Box className="MasterPage" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Helmet titleTemplate="%s" defaultTitle={`Admin Portal | ${tenant.title}`} />

      <AppChrome tenant={tenant}>
        <UpdateMonitor />

        <Box flexGrow={1}>
          <Authenticated>
            <AiChatProvider>
              <SpotlightProvider>
                <Spotlight />
                <RouteScanner>
                  <Routes>
                    <Route path="/" element={<AppRouteElement routePath={'/'} component={IndexPage} />} />
                    <Route path="/curation" element={<Navigate to="/curation/offer-list-order" replace />} />
                    <Route
                      path="/search-curation"
                      element={<Navigate to="/search-curation/search-dashboard" replace />}
                    />
                    <Route
                      path="/curation/search-dashboard"
                      element={<Navigate to="/search-curation/search-dashboard" replace />}
                    />
                    <Route
                      path="/curation/search-ranking"
                      element={<Navigate to="/search-curation/search-ranking" replace />}
                    />
                    <Route
                      path="/curation/blocked-offers"
                      element={<Navigate to="/search-curation/blocked-offers" replace />}
                    />
                    <Route
                      path="/curation/property-location-override"
                      element={<Navigate to="/search-curation/property-location-override" replace />}
                    />
                    <Route path="/curation/nl-search" element={<Navigate to="/search-curation/nl-search" replace />} />
                    <Route
                      path="/curation/restore-search-dashboard"
                      element={<Navigate to="/search-curation/restore-search-dashboard" replace />}
                    />
                    <Route
                      path="/curation/:tab"
                      element={<AppRouteElement routePath={'/curation/:tab'} component={CurationPageContainer} />}
                    />
                    <Route
                      path="/search-curation/:tab"
                      element={
                        <AppRouteElement routePath={'/search-curation/:tab'} component={CurationPageContainer} />
                      }
                    />
                    <Route
                      path="/experiences"
                      element={<AppRouteElement routePath={'/experiences'} component={ExperiencesPage} />}
                    />
                    <Route
                      path="/experience/:id_experience"
                      element={<AppRouteElement routePath={'/experience/:id_experience'} component={ExperiencesPage} />}
                    />
                    <Route
                      path="/offers/:id_offer"
                      element={<AppRouteElement routePath={'/offers/:id_offer'} component={OfferDetailContainer} />}
                    />
                    <Route path="/offers" element={<AppRouteElement routePath={'/offers'} component={OffersPage} />} />
                    <Route
                      path="/trip-planner"
                      element={<AppRouteElement routePath={'/trip-planner'} component={TripPlannerPage} />}
                    />
                    <Route path="/lab" element={<AppRouteElement routePath={'/lab'} component={LabPage} />} />
                    <Route path="/openai" element={<AppRouteElement routePath={'/openai'} component={OpenAIPage} />} />
                    <Route
                      path="/ask-richard/shared/:sessionId"
                      element={<AppRouteElement routePath={'/ask-richard/shared/:sessionId'} component={LeDataPage} />}
                    />
                    <Route
                      path="/ask-richard"
                      element={<AppRouteElement routePath={'/ask-richard'} component={LeDataPage} />}
                    />
                    <Route path="/data-chatbot" element={<Navigate to="/ask-richard" replace />} />
                    <Route
                      path="/attractions/:attractionId"
                      element={
                        <AppRouteElement routePath={'/attractions/:attractionId'} component={AttractionDetailPage} />
                      }
                    />
                    <Route
                      path="/attractions/:attractionId/image"
                      element={
                        <AppRouteElement
                          routePath={'/attractions/:attractionId/image'}
                          component={AttractionImageEditor}
                        />
                      }
                    />
                    <Route path="/shop" element={<AppRouteElement routePath={'/shop'} component={ShopPage} />} />
                    <Route
                      path="/trip-planner/classic"
                      element={<Navigate to={classicTripPlannerTabs[0].path} replace />}
                    />
                    {classicTripPlannerTabs.map((tab) => (
                      <Route
                        key={tab.path}
                        path={tab.path}
                        element={<AppRouteElement routePath={tab.path} component={TripPlannerClassicPage} />}
                      />
                    ))}
                    <Route path="/trip-planner/chat" element={<Navigate to={tripPlannerChatTabs[0].path} replace />} />
                    {tripPlannerChatTabs.map((tab) => (
                      <Route
                        key={tab.path}
                        path={tab.path}
                        element={<AppRouteElement routePath={tab.path} component={TripPlannerChatPage} />}
                      />
                    ))}
                    <Route
                      path="/trip-planner/classic/trip/:tripId"
                      element={
                        <AppRouteElement routePath={'/trip-planner/classic/trip/:tripId'} component={TripDetailPage} />
                      }
                    />
                    <Route
                      path="/trip-planner/trip/:tripId"
                      element={
                        <AppRouteElement
                          routePath={'/trip-planner/trip/:tripId'}
                          render={({ match }) => (
                            <Navigate to={`/trip-planner/classic/trip/${match.params.tripId}`} replace />
                          )}
                        />
                      }
                    />

                    <Route
                      path="/briefcase"
                      element={<AppRouteElement routePath={'/briefcase'} component={BriefCasePage} />}
                    />
                    <Route
                      path="/briefcase/:jobId"
                      element={<AppRouteElement routePath={'/briefcase/:jobId'} component={BriefCasePage} />}
                    />
                    <Route
                      path="/briefcase-template"
                      element={<AppRouteElement routePath={'/briefcase-template'} component={BriefCaseTemplatePage} />}
                    />
                    <Route
                      path="/edit-offers/:id_offer"
                      element={<AppRouteElement routePath={'/edit-offers/:id_offer'} component={OfferEditContainer} />}
                    />

                    <Route
                      path="/edit-offers/:vertical/:id_offer/search-orderings"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:vertical/:id_offer/search-orderings'}
                          component={OfferSearchOrderingContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/schedules/:brand"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/schedules/:brand'}
                          component={ScheduleEditContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/schedules"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/schedules'}
                          component={NewScheduleContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/bundle-packages"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/bundle-packages'}
                          component={BundlePackagesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/packages"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/packages'}
                          component={PackagesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/images"
                      element={
                        <AppRouteElement routePath={'/edit-offers/:id_offer/images'} component={ImagesEditContainer} />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/videos"
                      element={
                        <AppRouteElement routePath={'/edit-offers/:id_offer/videos'} component={VideosEditContainer} />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/lux-moments"
                      element={
                        <AppRouteElement routePath={'/edit-offers/:id_offer/lux-moments'} component={LuxMomentsPage} />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/room-policies"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/room-policies'}
                          component={RoomPolicyEditContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/structured-fine-print"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/structured-fine-print'}
                          component={StructuredFinePrintPage}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/badges"
                      element={
                        <AppRouteElement routePath={'/edit-offers/:id_offer/badges'} component={BadgesEditContainer} />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/traveller-detail-requirements"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/traveller-detail-requirements'}
                          component={TravellerDetailRequirementsEditPage}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/experiences"
                      element={
                        <AppRouteElement
                          routePath={'/edit-offers/:id_offer/experiences'}
                          component={ExperiencesEditContainer}
                        />
                      }
                    />

                    <Route path="/tours" element={<Navigate to="/tours/lists/le" replace />} />
                    <Route
                      path="/tours/lists/le"
                      element={
                        <AppRouteElement routePath={'/tours/lists/le'} render={renderToursContent(ToursLEListPage)} />
                      }
                    />
                    <Route
                      path="/tours/lists/ttc"
                      element={
                        <AppRouteElement routePath={'/tours/lists/ttc'} render={renderToursContent(ToursTTCListPage)} />
                      }
                    />
                    <Route
                      path="/tours/deals"
                      element={
                        <AppRouteElement routePath={'/tours/deals'} render={renderToursContent(TourDealsPage)} />
                      }
                    />
                    <Route
                      path="/tours/deals/create"
                      element={
                        <AppRouteElement
                          routePath={'/tours/deals/create'}
                          render={renderToursContent(TourDealPageCreate)}
                        />
                      }
                    />
                    <Route
                      path="/tours/deals/:id/edit"
                      element={
                        <AppRouteElement
                          routePath={'/tours/deals/:id/edit'}
                          render={renderToursContent(TourDealPageEdit)}
                        />
                      }
                    />
                    <Route
                      path="/tours/departures/insights"
                      element={
                        <AppRouteElement
                          routePath={'/tours/departures/insights'}
                          render={renderToursContent(ToursDepartureInsightsPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/manifests"
                      element={
                        <AppRouteElement
                          routePath={'/tours/manifests'}
                          render={renderToursContent(ToursManifestPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/notifications"
                      element={
                        <AppRouteElement
                          routePath={'/tours/notifications'}
                          render={renderToursContent(ToursNotificationsPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/details/:tour_id/*"
                      element={
                        <AppRouteElement
                          routePath={'/tours/details/:tour_id/*'}
                          render={renderToursContent(TourDetailsPage)}
                        />
                      }
                    />
                    {/* Backwards compatibility for the Chrome Extension */}
                    <Route
                      path="/tours/:tour_id"
                      element={
                        <AppRouteElement
                          routePath={'/tours/:tour_id'}
                          render={({ match }) => <Navigate to={`/tours/details/${match.params.tour_id}`} replace />}
                        />
                      }
                    />

                    <Route
                      path="/aws/jobs"
                      element={<AppRouteElement routePath={'/aws/jobs'} component={AwsJobsPage} />}
                    />
                    <Route
                      path="/bedbank/properties"
                      element={<AppRouteElement routePath={'/bedbank/properties'} component={BedbankPropertiesPage} />}
                    />
                    <Route
                      path="/bedbank/properties/:id"
                      element={
                        <AppRouteElement routePath={'/bedbank/properties/:id'} component={BedbankPropertiesEditPage} />
                      }
                    />
                    <Route
                      path="/flights"
                      element={<AppRouteElement routePath={'/flights'} component={FlightsPage} />}
                    />
                    <Route
                      path="/flights/credit-reservation"
                      element={
                        <AppRouteElement
                          routePath={'/flights/credit-reservation'}
                          component={CreditReservationListContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/credit-reservation/:id/details"
                      element={
                        <AppRouteElement
                          routePath={'/flights/credit-reservation/:id/details'}
                          component={CreditReservationDetailContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/credit-reservation/:id/edit"
                      element={
                        <AppRouteElement
                          routePath={'/flights/credit-reservation/:id/edit'}
                          component={CreditReservationEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/flight-deal/:id/edit"
                      element={
                        <AppRouteElement routePath={'/flights/flight-deal/:id/edit'} component={FlightsDealsEdit} />
                      }
                    />
                    <Route
                      path="/flights/campaigns/:id"
                      element={
                        <AppRouteElement routePath={'/flights/campaigns/:id'} component={FlightsCampaignsEdit} />
                      }
                    />
                    <Route
                      path="/flights/xml-logs-viewer/:id"
                      element={
                        <AppRouteElement routePath={'/flights/xml-logs-viewer/:id'} component={FlightsLogsViewer} />
                      }
                    />

                    <Route path="/users-list" element={<Navigate to="/system" replace />} />
                    <Route path="/users-list/stop-list/:id?" element={<Navigate to="/system/stop-list" replace />} />
                    <Route
                      path="/agencies"
                      element={<AppRouteElement routePath={'/agencies'} render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/agencies/agencies"
                      element={<AppRouteElement routePath={'/agencies/agencies'} render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/agencies/agency-submission"
                      element={
                        <AppRouteElement routePath={'/agencies/agency-submission'} render={() => <AgenciesPage />} />
                      }
                    />
                    <Route
                      path="/agencies/affiliations"
                      element={<AppRouteElement routePath={'/agencies/affiliations'} render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/users-list/agency-submission"
                      element={
                        <AppRouteElement routePath={'/users-list/agency-submission'} component={UsersPageContainer} />
                      }
                    />
                    <Route
                      path="/users-list/agency-submission/:id"
                      element={
                        <AppRouteElement
                          routePath={'/users-list/agency-submission/:id'}
                          component={UsersPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/users-list/agencies"
                      element={<AppRouteElement routePath={'/users-list/agencies'} component={UsersPageContainer} />}
                    />
                    <Route
                      path="/users-list/agencies/:id"
                      element={
                        <AppRouteElement routePath={'/users-list/agencies/:id'} component={UsersPageContainer} />
                      }
                    />
                    <Route
                      path="/users-list/affiliations"
                      element={
                        <AppRouteElement routePath={'/users-list/affiliations'} component={UsersPageContainer} />
                      }
                    />
                    <Route
                      path="/users-list/affiliations/:id"
                      element={
                        <AppRouteElement routePath={'/users-list/affiliations/:id'} component={UsersPageContainer} />
                      }
                    />

                    <Route
                      path="/find-by-phone/:phone?"
                      element={<AppRouteElement routePath={'/find-by-phone/:phone?'} component={FindByPhone} />}
                    />

                    <Route
                      path="/users/:id_user/custom-offers/create"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/custom-offers/create'}
                          component={CustomOffersCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/custom-offers/:offer_id"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/custom-offers/:offer_id'}
                          component={CustomOffersEditPage}
                        />
                      }
                    />

                    <Route
                      path="/users/:id_user/credits"
                      element={<AppRouteElement routePath={'/users/:id_user/credits'} component={CreditsContainer} />}
                    />
                    <Route
                      path="/users/:id_user/loyalty/:code"
                      element={
                        <AppRouteElement routePath={'/users/:id_user/loyalty/:code'} component={LoyaltyContainer} />
                      }
                    />
                    <Route
                      path="/users/:user_email"
                      element={<AppRouteElement routePath={'/users/:user_email'} component={UserPage} />}
                    />
                    <Route
                      path="/users/:id_user/history"
                      element={<AppRouteElement routePath={'/users/:id_user/history'} component={UserHistory} />}
                    />
                    <Route
                      path="/users/:id_user/new-order/:step?"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/new-order/:step?'}
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFER} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-flight-order/:step?"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/new-flight-order/:step?'}
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFLINE_FLIGHT} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-gift-card-order/:step?"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/new-gift-card-order/:step?'}
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_GIFT_CARD} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-insurance-order/:step?"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/new-insurance-order/:step?'}
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_INSURANCE} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/edit-orders/:id_orders/add-item/:step?"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/edit-orders/:id_orders/add-item/:step?'}
                          component={AddItemPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/edit-orders/:id_order/insurance/:id/:step"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/edit-orders/:id_order/insurance/:id/:step'}
                          component={UpdateInsuranceContainer}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/atol"
                      element={<AppRouteElement routePath={'/users/:id_user/atol'} component={AtolPage} />}
                    />
                    <Route
                      path="/atol-certificate/:id_atol_certificate"
                      element={
                        <AppRouteElement
                          routePath={'/atol-certificate/:id_atol_certificate'}
                          component={AtolCertificateDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/atol-certificate/:id_atol_certificate/edit"
                      element={
                        <AppRouteElement
                          routePath={'/atol-certificate/:id_atol_certificate/edit'}
                          component={AtolCertificateEditPage}
                        />
                      }
                    />

                    <Route
                      path="/purchases"
                      element={<AppRouteElement routePath={'/purchases'} component={PurchasesPageContainer} />}
                    />
                    <Route
                      path="/purchases/section/:section"
                      element={
                        <AppRouteElement routePath={'/purchases/section/:section'} component={PurchasesPageContainer} />
                      }
                    />
                    <Route
                      path="/purchases/booking-number/:bookingNumber"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/booking-number/:bookingNumber'}
                          component={PurchasesPageBookingNumberContainer}
                        />
                      }
                    />

                    <Route
                      path="/purchases/:id_orders/payment"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/:id_orders/payment'}
                          component={OrderPaymentSurcharge}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/gift/edit"
                      element={
                        <AppRouteElement routePath={'/purchases/:id_orders/gift/edit'} component={OrderGiftEditPage} />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/notes/add"
                      element={
                        <AppRouteElement routePath={'/purchases/:id_orders/notes/add'} component={OrderAddNotePage} />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/booking-details/*"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/:id_orders/booking-details/*'}
                          component={OrderBookingContainer}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/atol-details/*"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/:id_orders/atol-details/*'}
                          component={OrderAtolPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/*"
                      element={
                        <AppRouteElement routePath={'/purchases/:id_orders/*'} component={OrderDetailContainer} />
                      }
                    />
                    <Route
                      path="/edit-purchases/:id_orders"
                      element={
                        <AppRouteElement routePath={'/edit-purchases/:id_orders'} component={OrderEditContainer} />
                      }
                    />
                    <Route
                      path="/system/users/:id_user"
                      element={<AppRouteElement routePath={'/system/users/:id_user'} component={SystemUserPage} />}
                    />
                    <Route
                      path="/system"
                      element={<AppRouteElement routePath={'/system'} component={SystemPageContainer} />}
                    />
                    <Route
                      path="/system/find-similar-accounts"
                      element={
                        <AppRouteElement
                          routePath={'/system/find-similar-accounts'}
                          component={FindSimilarAccountsPage}
                        />
                      }
                    />
                    <Route
                      path="/system/account-deletions"
                      element={
                        <AppRouteElement routePath={'/system/account-deletions'} component={AccountDeletionsPage} />
                      }
                    />
                    <Route
                      path="/system/bulk-credit-allocation"
                      element={
                        <AppRouteElement
                          routePath={'/system/bulk-credit-allocation'}
                          component={BulkCreditAllocationPage}
                        />
                      }
                    />
                    <Route
                      path="/system/bulk-vcc-replacement"
                      element={
                        <AppRouteElement
                          routePath={'/system/bulk-vcc-replacement'}
                          component={BulkVccReplacementPage}
                        />
                      }
                    />
                    <Route
                      path="/system/credit-reversals"
                      element={
                        <AppRouteElement routePath={'/system/credit-reversals'} component={CreditReversalsPage} />
                      }
                    />
                    <Route
                      path="/system/cs-analysis"
                      element={<AppRouteElement routePath={'/system/cs-analysis'} component={CSAnalysisPage} />}
                    />
                    <Route
                      path="/system/admin-users"
                      element={
                        <AppRouteElement routePath={'/system/admin-users'} component={AdminUsersPageContainer} />
                      }
                    />
                    <Route
                      path="/system/stop-list"
                      element={<AppRouteElement routePath={'/system/stop-list'} component={StoplistPage} />}
                    />
                    <Route
                      path="/system/reservation-remediation"
                      element={
                        <AppRouteElement
                          routePath={'/system/reservation-remediation'}
                          component={ReservationRemediation}
                        />
                      }
                    />
                    <Route
                      path="/system/audit-logs"
                      element={<AppRouteElement routePath={'/system/audit-logs'} component={AuditLogSearch} />}
                    />
                    <Route
                      path="/system/permissions/:role"
                      element={
                        <AppRouteElement routePath={'/system/permissions/:role'} component={PermissionsContainer} />
                      }
                    />
                    <Route
                      path="/system/settings"
                      element={<AppRouteElement routePath={'/system/settings'} component={Settings} />}
                    />
                    <Route
                      path="/system/image-uploader"
                      element={
                        <AppRouteElement routePath={'/system/image-uploader'} component={ImageUploaderUtilityPage} />
                      }
                    />
                    <Route
                      path="/system/ai-image-manager/:tab?/:batchId?"
                      element={
                        <AppRouteElement
                          routePath={'/system/ai-image-manager/:tab?/:batchId?'}
                          component={AIImageManagerPage}
                        />
                      }
                    />
                    <Route
                      path="/system/sales-fx-hedging"
                      element={<AppRouteElement routePath={'/system/sales-fx-hedging'} component={SalesFxHedging} />}
                    />
                    <Route
                      path="/system/targeted-banner-campaigns"
                      element={
                        <AppRouteElement
                          routePath={'/system/targeted-banner-campaigns'}
                          component={SiteBannerCampaignsPage}
                        />
                      }
                    />
                    <Route
                      path="/system/targeted-banner-campaigns/create"
                      element={
                        <AppRouteElement
                          routePath={'/system/targeted-banner-campaigns/create'}
                          component={SiteBannerCampaignCreate}
                        />
                      }
                    />
                    <Route
                      path="/system/targeted-banner-campaigns/:campaignId"
                      element={
                        <AppRouteElement
                          routePath={'/system/targeted-banner-campaigns/:campaignId'}
                          component={SiteBannerCampaignEdit}
                        />
                      }
                    />

                    {!isProduction && (
                      <Route
                        path="/system/training-account-provisioning"
                        element={
                          <AppRouteElement
                            routePath={'/system/training-account-provisioning'}
                            component={TrainingAccountProvisioningPage}
                          />
                        }
                      />
                    )}
                    <Route
                      path="/vendors"
                      element={<AppRouteElement routePath={'/vendors'} component={VendorsPageContainer} />}
                    />
                    <Route
                      path="/vendors/chains"
                      element={<AppRouteElement routePath={'/vendors/chains'} component={ChainsListPage} />}
                    />
                    <Route
                      path="/vendors/chains/:chainId"
                      element={<AppRouteElement routePath={'/vendors/chains/:chainId'} component={ChainDetailPage} />}
                    />
                    <Route
                      path="/vendors/:id_vendor"
                      element={<AppRouteElement routePath={'/vendors/:id_vendor'} component={VendorsDetailContainer} />}
                    />
                    <Route
                      path="/vendors/:id_vendor/partner-central"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/partner-central'}
                          component={VendorsPartnerCentralPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/chain/:id_parent"
                      element={
                        <AppRouteElement routePath={'/vendors/chain/:id_parent'} component={ChainsDetailContainer} />
                      }
                    />
                    <Route
                      path="/vendors/chain/:id_parent/settings"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/chain/:id_parent/settings'}
                          component={EditChainSettingsPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties'}
                          component={PropertyNewContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property'}
                          component={PropertyPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-property/:id_property"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/edit-property/:id_property'}
                          component={PropertyEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/inclusions"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/inclusions'}
                          component={VendorInclusionsPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans-groups"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/rate-plans-groups'}
                          component={RatePlansGroupEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/rate-plans'}
                          component={RatePlanNewContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/rate-plans/:id_rate_plan'}
                          component={RatePlanPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan/inclusions"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/rate-plans/:id_rate_plan/inclusions'}
                          component={RatePlanInclusionsPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-rate-plan/:id_rate_plan"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/edit-rate-plan/:id_rate_plan'}
                          component={RatePlanEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-curated-content"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-curated-content'}
                          component={CuratedContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-room-types'}
                          component={RoomTypesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-images"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-images'}
                          component={ImagesEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-videos"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-videos'}
                          component={VideosEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate"
                      element={
                        <AppRouteElement
                          routePath={
                            '/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate'
                          }
                          component={RoomTypeEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate/dates"
                      element={
                        <AppRouteElement
                          routePath={
                            '/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate/dates'
                          }
                          component={RoomTypeDatesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types-groups"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/room-types-groups'}
                          component={RoomTypesGroupsPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types-groups"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-room-types-groups'}
                          component={RoomTypesGroupEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-reviews"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-property-reviews'}
                          component={PropertyReviewsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-content"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-property-taxes-content'}
                          component={PropertyTaxesContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-commissionable-taxes-content"
                      element={
                        <AppRouteElement
                          routePath={
                            '/vendors/:id_vendor/properties/:id_property/edit-property-commissionable-taxes-content'
                          }
                          component={PropertyCommissionableTaxesContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-fees"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/properties/:id_property/edit-property-taxes-fees'}
                          component={PropertyTaxesFeesEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/tours/:id_tour'}
                          component={TourPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-tour/:id_tour"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/edit-tour/:id_tour'}
                          component={TourEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours"
                      element={<AppRouteElement routePath={'/vendors/:id_vendor/tours'} component={TourNewContainer} />}
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-options"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/tours/:id_tour/edit-tour-options'}
                          component={TourOptionsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/tour-options/:id_tour_option/dates"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/tours/:id_tour/tour-options/:id_tour_option/dates'}
                          component={TourOptionDatesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-legs"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/tours/:id_tour/edit-tour-legs'}
                          component={TourLegsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-reviews"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/tours/:id_tour/edit-tour-reviews'}
                          component={TourReviewsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/addons/:id_addon"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/addons/:id_addon'}
                          component={VendorAddonDetails}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/addons/:id_addon/deal-options/:id_addon_deal_option/edit"
                      element={
                        <AppRouteElement
                          routePath={'/vendors/:id_vendor/addons/:id_addon/deal-options/:id_addon_deal_option/edit'}
                          component={VendorAddonEdit}
                        />
                      }
                    />

                    <Route
                      path="/bookings/:id_orders"
                      element={<AppRouteElement routePath={'/bookings/:id_orders'} component={BookingPage} />}
                    />

                    <Route
                      path="/marketing/voucher-display-config/new"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/voucher-display-config/new'}
                          component={VoucherDisplayConfigDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/voucher-display-config/:voucherDisplayConfigId"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/voucher-display-config/:voucherDisplayConfigId'}
                          component={VoucherDisplayConfigDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/vouchers/new"
                      element={<AppRouteElement routePath={'/marketing/vouchers/new'} component={VoucherFormPage} />}
                    />
                    <Route
                      path="/marketing/vouchers/quick-create"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/vouchers/quick-create'}
                          render={() => (isPromoAdmin ? <QuickCreateVoucherPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/vouchers/:voucherId"
                      element={
                        <AppRouteElement routePath={'/marketing/vouchers/:voucherId'} component={VoucherFormPage} />
                      }
                    />
                    <Route
                      path="/marketing/voucher-auto-assign-rules/create"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/voucher-auto-assign-rules/create'}
                          render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/voucher-auto-assign-rules/:ruleId"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/voucher-auto-assign-rules/:ruleId'}
                          render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/:tab?"
                      element={<AppRouteElement routePath={'/marketing/:tab?'} component={MarketingPage} />}
                    />
                    <Route
                      path="/marketing/video-gen/edit"
                      element={<AppRouteElement routePath={'/marketing/video-gen/edit'} component={VideoEditPage} />}
                    />
                    <Route
                      path="/marketing/video-gen/preview/output-video/:videoId"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/video-gen/preview/output-video/:videoId'}
                          component={MediaPreviewPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/video-gen/preview/asset/:videoId"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/video-gen/preview/asset/:videoId'}
                          component={MediaPreviewPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/"
                      element={
                        <AppRouteElement routePath={'/marketing/google-ads/customer/'} component={CustomerListView} />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/creative-ai"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/creative-ai'}
                          component={AdCreativeAIAssistantContainer}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/bid-modification-history/"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/bid-modification-history/'}
                          component={BidModificationHistory}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/hotel-bid-modification-history/"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/hotel-bid-modification-history/'}
                          component={HotelBidModificationHistory}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/hotel-bid-failure-log"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/hotel-bid-failure-log'}
                          component={HotelBidFailureLog}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/customer/:customerId/campaign'}
                          component={CustomerCampaignListView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/customer/:customerId/campaign/:campaignId'}
                          component={CampaignDetailsView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group"
                      element={
                        <AppRouteElement
                          routePath={'/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group'}
                          component={AdGroupListView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group/:adGroupId"
                      element={
                        <AppRouteElement
                          routePath={
                            '/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group/:adGroupId'
                          }
                          component={AdGroupDetailsView}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/properties"
                      element={
                        <AppRouteElement routePath={'/accommodation/properties'} component={AccommodationHomePage} />
                      }
                    />
                    <Route
                      path="/accommodation/properties/:propertyId/*"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/properties/:propertyId/*'}
                          component={AccommodationPropertyDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/bundles/new"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/bundles/new'}
                          component={AccommodationBundleCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/bundles/:bundleId/:tab?/*"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/bundles/:bundleId/:tab?/*'}
                          component={AccommodationBundleEditPage}
                        />
                      }
                    />
                    <Route
                      path="/email/render"
                      element={<AppRouteElement routePath={'/email/render'} component={RenderEmail} />}
                    />
                    <Route
                      path="/email/sent"
                      element={<AppRouteElement routePath={'/email/sent'} component={EmailPageContainer} />}
                    />

                    <Route
                      path="/comped/:id_comped"
                      element={
                        <AppRouteElement routePath={'/comped/:id_comped'} component={CompedPackagesDetailContainer} />
                      }
                    />
                    <Route
                      path="/promos/code/:id_code"
                      element={
                        <AppRouteElement routePath={'/promos/code/:id_code'} component={PromoDetailContainerV3} />
                      }
                    />
                    <Route
                      path="/promos/:id_promo"
                      element={<AppRouteElement routePath={'/promos/:id_promo'} component={PromoDetailContainerV3} />}
                    />
                    <Route
                      path="/badges"
                      element={<AppRouteElement routePath={'/badges'} component={BadgesContainer} />}
                    />
                    <Route
                      path="/badges/:id_badge"
                      element={<AppRouteElement routePath={'/badges/:id_badge'} component={BadgesDetailContainer} />}
                    />

                    <Route
                      path="/profile"
                      element={<AppRouteElement routePath={'/profile'} component={ProfilePage} />}
                    />
                    <Route path="/loyalty" element={<Navigate to="/societe/custom-rule" replace />} />
                    <Route
                      path="/loyalty/:section/*"
                      element={
                        <AppRouteElement
                          routePath={'/loyalty/:section/*'}
                          render={({ match }) => <Navigate to={`/societe/${match.params.section}`} replace />}
                        />
                      }
                    />
                    <Route path="/societe" element={<Navigate to="/societe/custom-rule" replace />} />
                    <Route
                      path="/societe/:section"
                      element={<AppRouteElement routePath={'/societe/:section'} component={LoyaltyPageContainer} />}
                    />
                    <Route
                      path="/finance"
                      element={<AppRouteElement routePath={'/finance'} component={FinancePageContainer} />}
                    />
                    <Route
                      path="/finance/offers/:id_offer"
                      element={
                        <AppRouteElement
                          routePath={'/finance/offers/:id_offer'}
                          component={FinanceOfferDetailContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor/:id_offer_payment"
                      element={
                        <AppRouteElement
                          routePath={'/finance/offers/:id_offer/offer-payments/:id_vendor/:id_offer_payment'}
                          component={VendorOfferPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor"
                      element={
                        <AppRouteElement
                          routePath={'/finance/offers/:id_offer/offer-payments/:id_vendor'}
                          component={VendorOfferPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/booking-payments/:id_vendor"
                      element={
                        <AppRouteElement
                          routePath={'/finance/offers/:id_offer/booking-payments/:id_vendor'}
                          component={VendorBookingPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/:section/:report/*"
                      element={
                        <AppRouteElement routePath={'/finance/:section/:report/*'} component={FinancePageContainer} />
                      }
                    />
                    <Route
                      path="/finance/:section/*"
                      element={<AppRouteElement routePath={'/finance/:section/*'} component={FinancePageContainer} />}
                    />

                    <Route path="/email" element={<AppRouteElement routePath={'/email'} component={EmailPage} />} />
                    <Route
                      path="/email/:tab?"
                      element={<AppRouteElement routePath={'/email/:tab?'} component={EmailPage} />}
                    />

                    <Route
                      path="/customer-communication/hero-planner/schedules/:scheduleEditorId"
                      element={
                        <AppRouteElement
                          routePath={'/customer-communication/hero-planner/schedules/:scheduleEditorId'}
                          component={HeroPlannerScheduleEditorPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/hero-planner/schedules"
                      element={
                        <AppRouteElement
                          routePath={'/customer-communication/hero-planner/schedules'}
                          component={HeroPlannerSchedulesListPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/banner-upload"
                      element={
                        <AppRouteElement
                          routePath={'/customer-communication/banner-upload'}
                          component={BannerUploadPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/email/sent"
                      element={
                        <AppRouteElement
                          routePath={'/customer-communication/email/sent'}
                          component={MarketingEmailsPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/email/render"
                      element={
                        <AppRouteElement
                          routePath={'/customer-communication/email/render'}
                          component={RenderMarketingEmailPage}
                        />
                      }
                    />

                    <Route path="/health" element={<AppRouteElement routePath={'/health'} component={HealthPage} />} />

                    <Route
                      path="/cruises/:section"
                      element={<AppRouteElement routePath={'/cruises/:section'} component={CruisesPage} />}
                    />
                    <Route
                      path="/cruises/offer/:id_offer"
                      element={<AppRouteElement routePath={'/cruises/offer/:id_offer'} component={CruisePageDetails} />}
                    />
                    <Route
                      path="/cruises/offer/:offerId/images"
                      element={
                        <AppRouteElement routePath={'/cruises/offer/:offerId/images'} component={CruisePageImages} />
                      }
                    />
                    <Route
                      path="/lere"
                      element={<AppRouteElement routePath={'/lere'} component={LereAdminPageContainer} />}
                    />
                    <Route
                      path="/lere/:section"
                      element={<AppRouteElement routePath={'/lere/:section'} component={LereAdminPageContainer} />}
                    />
                    <Route
                      path="/cruises/ships/:shipId"
                      element={<AppRouteElement routePath={'/cruises/ships/:shipId'} component={ShipImageEditor} />}
                    />
                    <Route
                      path="/cruises/ships/:shipId/images"
                      element={
                        <AppRouteElement routePath={'/cruises/ships/:shipId/images'} component={ShipImageEditor} />
                      }
                    />
                    <Route
                      path="/cruises/ships/:shipId/amenities"
                      element={
                        <AppRouteElement routePath={'/cruises/ships/:shipId/amenities'} component={ShipImageEditor} />
                      }
                    />
                    <Route
                      path="/cruises/cruise-lines/order"
                      element={
                        <AppRouteElement routePath={'/cruises/cruise-lines/order'} component={CruiseLineOrdering} />
                      }
                    />
                    <Route
                      path="/cruises/cruise-lines/:cruiseLineId"
                      element={
                        <AppRouteElement routePath={'/cruises/cruise-lines/:cruiseLineId'} component={CruiseLineEdit} />
                      }
                    />
                    <Route
                      path="/cruises/ports/:portId"
                      element={<AppRouteElement routePath={'/cruises/ports/:portId'} component={CruisePortsEditPage} />}
                    />
                    <Route
                      path="/cruises/rate-code-management"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/rate-code-management'}
                          component={RateCodeManagementPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/rate-code-management/:rateCodeId/edit"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/rate-code-management/:rateCodeId/edit'}
                          component={EditRateCodePage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/sailings/:id_sailing"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/sailings/:id_sailing'}
                          component={CruiseSailingDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories/:id_cabin"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/cabin-categories/:id_cabin'}
                          component={CruiseCabinCategoriesDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories-groups/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/cabin-categories-groups/create'}
                          component={CreateCabinCategoriesGroupPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/create"
                      element={
                        <AppRouteElement routePath={'/cruises/bundles/create'} component={CruiseBundlesCreate} />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId"
                      element={
                        <AppRouteElement routePath={'/cruises/bundles/edit/:bundleId'} component={CruiseBundlesEdit} />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/items'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/items/create'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/items/create/:actionId'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/edit"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/items/edit'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/items/edit/:actionId'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/filters'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/filters/create'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/filters/create/:actionId'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/edit"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/filters/edit'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bundles/edit/:bundleId/filters/edit/:actionId'}
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/create"
                      element={
                        <AppRouteElement routePath={'/cruises/inclusions/create'} component={CruiseInclusionsCreate} />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/items'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/items/create'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/items/create/:actionId'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/edit"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/items/edit'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/items/edit/:actionId'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/filters'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/filters/create'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/filters/create/:actionId'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/edit"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/filters/edit'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/inclusions/edit/:inclusionId/filters/edit/:actionId'}
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/deals"
                      element={<AppRouteElement routePath={'/cruises/deals'} component={DealPageList} />}
                    />
                    <Route
                      path="/cruises/deals/create"
                      element={<AppRouteElement routePath={'/cruises/deals/create'} component={DealPageCreate} />}
                    />
                    <Route
                      path="/cruises/deals/ordering"
                      element={<AppRouteElement routePath={'/cruises/deals/ordering'} component={DealPageOrdering} />}
                    />
                    <Route
                      path="/cruises/deals/:id/edit"
                      element={<AppRouteElement routePath={'/cruises/deals/:id/edit'} component={DealPageEdit} />}
                    />
                    <Route
                      path="/cruises/deals/:id/edit/inclusions/:action?"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/deals/:id/edit/inclusions/:action?'}
                          component={DealPageEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories-groups/:id_group"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/cabin-categories-groups/:id_group'}
                          component={CabinCategoriesGroupOverviewPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bookings/:booking_id/logs"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/bookings/:booking_id/logs'}
                          component={CruisesBookingLogsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/booking-logs"
                      element={<AppRouteElement routePath={'/cruises/booking-logs'} component={CruisesBookingsPage} />}
                    />
                    <Route
                      path="/cruises/deal-crawler/create"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/deal-crawler/create'}
                          component={CruiseDealCrawlerCreate}
                        />
                      }
                    />
                    <Route
                      path="/cruises/deal-crawler/:id/view"
                      element={
                        <AppRouteElement
                          routePath={'/cruises/deal-crawler/:id/view'}
                          component={CruiseDealCrawlerView}
                        />
                      }
                    />
                    <Route
                      path={DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}
                      element={
                        <AppRouteElement
                          routePath={DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}
                          component={DealCrawlerPageCreationSelection}
                        />
                      }
                    />
                    <Route
                      path={DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}
                      element={
                        <AppRouteElement
                          routePath={DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}
                          component={DealCrawlerPageCreationConfirm}
                        />
                      }
                    />

                    <Route
                      path="/lebusiness"
                      element={<AppRouteElement routePath={'/lebusiness'} component={BusinessMainPage} />}
                    />
                    <Route
                      path="/lebusiness/:businessId/:tab"
                      element={
                        <AppRouteElement routePath={'/lebusiness/:businessId/:tab'} component={BusinessTabContainer} />
                      }
                    />

                    <Route path="/invoice-tools" element={<Navigate to="/invoice-tools/invoice-generator" replace />} />
                    <Route
                      path="/invoice-tools/:tab"
                      element={
                        <AppRouteElement routePath={'/invoice-tools/:tab'} component={InvoiceToolsPageContainer} />
                      }
                    />

                    <Route
                      path="/support-assistant/:tab?"
                      element={
                        <AppRouteElement routePath={'/support-assistant/:tab?'} component={SupportAssistantPage} />
                      }
                    />

                    <Route
                      path="/amenities"
                      element={<AppRouteElement routePath={'/amenities'} component={AmenitiesPageContainer} />}
                    />
                    <Route
                      path="/amenities/:id_amenity"
                      element={<AppRouteElement routePath={'/amenities/:id_amenity'} component={AmenityEdit} />}
                    />
                    <Route
                      path="/amenity-groups/:id_group"
                      element={<AppRouteElement routePath={'/amenity-groups/:id_group'} component={AmenityGroupEdit} />}
                    />

                    <Route
                      path="/commercial-ops/price-parity/scheduler/jobs/:jobId"
                      element={
                        <AppRouteElement
                          routePath={'/commercial-ops/price-parity/scheduler/jobs/:jobId'}
                          component={ScheduledPriceSummaryJobDetails}
                        />
                      }
                    />
                    <Route
                      path="/commercial-ops/:section?/:subsection?"
                      element={
                        <AppRouteElement
                          routePath={'/commercial-ops/:section?/:subsection?'}
                          component={CommercialOpsPage}
                        />
                      }
                    />

                    <Route
                      path="/opportunity/:opportunityId"
                      element={
                        <AppRouteElement routePath={'/opportunity/:opportunityId'} component={OpportunityContainer} />
                      }
                    />

                    <Route
                      path="/example"
                      element={<AppRouteElement routePath={'/example'} component={ExamplePage} />}
                    />
                    <Route
                      path="/site-banner-campaigns"
                      element={
                        <AppRouteElement routePath={'/site-banner-campaigns'} component={SiteBannerCampaignsPage} />
                      }
                    />
                    <Route
                      path="/site-banner-campaigns/create"
                      element={
                        <AppRouteElement
                          routePath={'/site-banner-campaigns/create'}
                          component={SiteBannerCampaignCreate}
                        />
                      }
                    />
                    <Route
                      path="/site-banner-campaigns/:campaignId"
                      element={
                        <AppRouteElement
                          routePath={'/site-banner-campaigns/:campaignId'}
                          component={SiteBannerCampaignEdit}
                        />
                      }
                    />
                    <Route
                      path="/ai-summary"
                      element={<AppRouteElement routePath={'/ai-summary'} component={AiSummaryStatsPage} />}
                    />
                    <Route
                      path="/sms-verify-stats"
                      element={<AppRouteElement routePath={'/sms-verify-stats'} component={SmsVerifyStatsPage} />}
                    />
                    <Route
                      path="/system/cs-agent-actions-dashboard"
                      element={
                        <AppRouteElement routePath={'/system/cs-agent-actions-dashboard'} component={CsDashboardPage} />
                      }
                    />
                    <Route
                      path="/sms-verify-stats/update-agents"
                      element={
                        <AppRouteElement routePath={'/sms-verify-stats/update-agents'} component={UpdateCsAgentsPage} />
                      }
                    />
                    <Route
                      path="/users/:id_user/offer-deep-link"
                      element={
                        <AppRouteElement
                          routePath={'/users/:id_user/offer-deep-link'}
                          component={CreateOfferDeepLinkForm}
                        />
                      }
                    />
                    <Route
                      path="/trip-planner/classic/trip/:tripId/prompts/details"
                      element={
                        <AppRouteElement
                          routePath={'/trip-planner/classic/trip/:tripId/prompts/details'}
                          component={CreateTripPage}
                        />
                      }
                    />
                    <Route
                      path="/trip-planner/trip/:tripId/prompts/details"
                      element={
                        <AppRouteElement
                          routePath={'/trip-planner/trip/:tripId/prompts/details'}
                          render={({ match }) => (
                            <Navigate
                              to={`/trip-planner/classic/trip/${match.params.tripId}/prompts/details`}
                              replace
                            />
                          )}
                        />
                      }
                    />
                    <Route
                      path="/corporate-events/companies"
                      element={
                        <AppRouteElement routePath={'/corporate-events/companies'} component={CompaniesListPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/companies/create"
                      element={
                        <AppRouteElement routePath={'/corporate-events/companies/create'} component={CompanyFormPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/companies/:id/edit"
                      element={
                        <AppRouteElement
                          routePath={'/corporate-events/companies/:id/edit'}
                          component={CompanyFormPage}
                        />
                      }
                    />
                    <Route
                      path="/corporate-events/events"
                      element={<AppRouteElement routePath={'/corporate-events/events'} component={EventsListPage} />}
                    />
                    <Route
                      path="/corporate-events/events/create"
                      element={
                        <AppRouteElement routePath={'/corporate-events/events/create'} component={EventFormPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/events/:id/edit"
                      element={
                        <AppRouteElement routePath={'/corporate-events/events/:id/edit'} component={EventFormPage} />
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </RouteScanner>
              </SpotlightProvider>
              <AiChatDrawer />
            </AiChatProvider>
          </Authenticated>

          <NotAuthenticated>
            <Routes>
              <Route path="*" element={<AppRouteElement routePath={'*'} component={LoginPage} />} />
            </Routes>
          </NotAuthenticated>
        </Box>

        <Footer />
      </AppChrome>
    </Box>
  );
}
